/**
 * Visitor PWA client for /api/protocol/landing.
 *
 * Fetches the AI-composed landing state in parallel with /api/status/:qrCode.
 * On arrival before the late-arrival threshold, callers swap in the AI hero
 * + chips + placeholder. On error/timeout/late-arrival, the static fallback
 * (Direction 1 layout) stays in place.
 *
 * Session token (UUID v4, sessionStorage-bound) is included on every
 * request so the server can compose memory cues across same-session
 * touchpoint scans.
 */

import type { LandingResponse } from '@tendedloop/shared';
import {
  LANDING_PROTOCOL_ENDPOINT,
  LANDING_CLIENT_TIMEOUT_MS,
  LANDING_LATE_ARRIVAL_MS,
  LANDING_FALLBACK_CACHE_KEY,
  VISITOR_SESSION_HEADER,
  VISITOR_SESSION_STORAGE_KEY,
} from '@tendedloop/shared';

function getOrCreateSessionToken(): string {
  try {
    const existing = window.sessionStorage?.getItem(VISITOR_SESSION_STORAGE_KEY);
    if (existing) return existing;
    const fresh =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? (crypto as { randomUUID: () => string }).randomUUID()
        : `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
          });
    window.sessionStorage?.setItem(VISITOR_SESSION_STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // sessionStorage unavailable (private mode quirks etc.) — return an
    // ephemeral token; server still treats it as a one-off.
    return 'no-storage';
  }
}

export interface FetchLandingArgs {
  qrCode: string;
  language: string;
  apiUrl: string;
  /** Page-load timestamp (Date.now()). Used to enforce the late-arrival rule. */
  pageLoadedAt: number;
}

export type FetchLandingResult =
  | { ok: true; response: LandingResponse }
  | { ok: false; reason: 'aborted' | 'late' | 'http' | 'parse' | 'no-network' };

export async function fetchLanding(args: FetchLandingArgs): Promise<FetchLandingResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LANDING_CLIENT_TIMEOUT_MS);
  try {
    const url = `${args.apiUrl}${LANDING_PROTOCOL_ENDPOINT}?qrCode=${encodeURIComponent(args.qrCode)}&lang=${encodeURIComponent(args.language)}`;
    const res = await fetch(url, {
      method: 'GET',
      signal: controller.signal,
      headers: { [VISITOR_SESSION_HEADER]: getOrCreateSessionToken() },
    });

    // Late-arrival discard: even if the response is in flight, if it
    // resolves later than LANDING_LATE_ARRIVAL_MS post-page-load we
    // throw away the result — a 4-second swap is worse than no swap.
    if (Date.now() - args.pageLoadedAt > LANDING_LATE_ARRIVAL_MS) {
      return { ok: false, reason: 'late' };
    }

    if (!res.ok) return { ok: false, reason: 'http' };
    const body = (await res.json()) as { success?: boolean; data?: LandingResponse };
    if (!body?.success || !body.data) return { ok: false, reason: 'parse' };
    // The server returns a static-fallback LandingResponse (English hero +
    // placeholder, not localised) when context-aware landing is off, over the
    // daily spend cap, or the composer/validation fails. Treat it as "no
    // landing" so the client keeps its own Direction-1 layout, whose greeting
    // and placeholder come from localised i18n keys. Swapping in the server
    // fallback would leak English onto non-English views.
    if (body.data.cacheKey === LANDING_FALLBACK_CACHE_KEY) return { ok: false, reason: 'http' };
    return { ok: true, response: body.data };
  } catch (err) {
    const aborted = (err as { name?: string })?.name === 'AbortError';
    return { ok: false, reason: aborted ? 'aborted' : 'no-network' };
  } finally {
    clearTimeout(timer);
  }
}
